// ---------------------------------------------------------------------------
// Janela do browser à parte para um painel ("destacar").
//
// Mecanismo: `window.open` + `createPortal` do React para dentro do
// documento novo. A janela nasce do MESMO contexto JS, logo partilha o
// store do Zustand, os módulos carregados e a API do viewer: não há IPC,
// não há serialização, não há segunda instância da aplicação. Clicar num
// botão na janela destacada mexe no 3D da janela principal, porque é o
// mesmo código a correr sobre o mesmo estado.
//
// O que NÃO é partilhado, e é o que este ficheiro trata:
//   - o documento e o CSS: clonam-se os nós de estilo do `head` (em dev
//     são `<style>` injectados pelo Vite, em produção `<link
//     rel="stylesheet">`);
//   - o tema: o atributo `data-tema` da raiz é copiado e seguido (mudar
//     o tema na janela principal muda-o também na destacada);
//   - a vida da janela: fechar ou recarregar a página principal fecha a
//     destacada (não fica uma janela órfã com um painel morto), e fechar
//     a destacada avisa quem a abriu, para o painel voltar ao sítio.
//
// O que fica a cargo de quem usa (e é por isso que o hook devolve `win`):
//   - `document.elementFromPoint`, `addEventListener('mousedown')` e afins
//     são por documento: usar o `ownerDocument` do nó;
//   - listeners de teclado em `window` são por janela: usar `win`;
//   - `prompt`/`confirm`/`alert` aparecem na janela de quem os chama.
// Os eventos do React funcionam sem nada especial: o React regista os
// listeners no contentor do portal (`preparePortalMount`).
// ---------------------------------------------------------------------------

/** Tamanho por omissão de uma janela destacada de painel. */
export const FEATURES_PAINEL = 'width=520,height=760,resizable=yes,scrollbars=yes'

/** Aviso quando o browser bloqueia a janela. `oQue` leva o artigo: "a
 *  Section box", "o tópico". */
export function avisoJanelaBloqueada(oQue: string): string {
  return (
    `O browser bloqueou a janela d${oQue}. Autoriza as janelas emergentes ` +
    'deste sítio (ícone à direita da barra de endereço) e tenta outra vez. ' +
    'O painel ficou no sítio de sempre.'
  )
}

/** Um Escape que deve fechar o painel destacado: não quando se está a
 *  escrever num campo (aí o Escape é do campo).
 *
 *  Não se usa `instanceof HTMLElement`: os nós da janela destacada são
 *  da outra janela e falhavam sempre essa verificação. */
export function escapeQueFechaPainel(e: Pick<KeyboardEvent, 'key' | 'target'>): boolean {
  if (e.key !== 'Escape') return false
  const t = e.target as { tagName?: string; isContentEditable?: boolean } | null
  const tag = t?.tagName?.toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false
  return !t?.isContentEditable
}

/** Atributo da raiz do documento que o CSS lê para o tema (ver
 *  `core/tema.ts`). */
export const ATRIBUTO_TEMA = 'data-tema'

/** Clona para `to` os nós de estilo do `head` de `from`. Devolve quantos
 *  clonou (0 = a janela ia sair sem CSS nenhum). */
export function copyStyleNodes(from: Document, to: Document): number {
  const nodes = from.head.querySelectorAll('link[rel="stylesheet"], style')
  nodes.forEach((node) => {
    to.head.appendChild(node.cloneNode(true))
  })
  return nodes.length
}

/** Copia o tema da raiz de `from` para a de `to`, e o `color-scheme` que
 *  lhe corresponde (barras de deslocamento e controlos nativos). */
export function copiarTema(from: Document, to: Document): void {
  const tema = from.documentElement.getAttribute(ATRIBUTO_TEMA)
  if (tema) to.documentElement.setAttribute(ATRIBUTO_TEMA, tema)
  else to.documentElement.removeAttribute(ATRIBUTO_TEMA)
  to.body.style.colorScheme = tema === 'claro' ? 'light' : 'dark'
}

/** Segue as mudanças de tema de `from` em `to`. Devolve a função que
 *  pára de seguir. Sem `MutationObserver` (ambiente sem DOM completo),
 *  copia uma vez e não segue. */
export function espelharTema(from: Document, to: Document): () => void {
  copiarTema(from, to)
  const Observador = from.defaultView?.MutationObserver ?? globalThis.MutationObserver
  if (typeof Observador !== 'function') return () => {}
  const obs = new Observador(() => copiarTema(from, to))
  obs.observe(from.documentElement, {
    attributes: true,
    attributeFilter: [ATRIBUTO_TEMA],
  })
  return () => obs.disconnect()
}

/** Prepara o documento da janela nova e devolve o contentor do portal.
 *
 *  A altura explícita em `html` e `body` não é decoração: há painéis
 *  (o desenho 2D) que são `absolute inset-0` dentro de um contentor em
 *  coluna, e sem uma cadeia de alturas até à raiz saíam com zero pixéis
 *  de alto.
 *
 *  Uma janela reaproveitada (o browser devolve a que já existe com o
 *  mesmo nome) vem com o conteúdo antigo: limpa-se o corpo antes. */
export function prepareDetachedDocument(
  doc: Document,
  title: string,
): HTMLDivElement {
  doc.title = title
  const root = doc.documentElement as HTMLElement
  root.style.height = '100%'
  const body = doc.body
  while (body.firstChild) body.removeChild(body.firstChild)
  body.style.margin = '0'
  body.style.height = '100%'
  body.style.overflow = 'hidden'
  body.style.background = 'var(--color-bg)'
  body.style.color = 'var(--color-text)'
  body.style.fontFamily = 'var(--font-sans)'
  body.style.colorScheme = 'dark'

  const div = doc.createElement('div')
  div.style.height = '100vh'
  div.style.display = 'flex'
  div.style.flexDirection = 'column'
  body.appendChild(div)
  return div
}

// Janelas destacadas abertas agora, para saber em qual está a pessoa.
const abertas = new Set<Window>()

/** A janela onde a pessoa está a trabalhar: uma destacada com o foco, ou
 *  a principal. Serve para as caixas de diálogo (`confirm`) de código que
 *  não sabe em que janela o seu painel está: a caixa tem de aparecer onde
 *  se carregou no botão, não noutro ecrã. */
export function janelaActiva(principal: Window = window): Window {
  for (const w of abertas) {
    try {
      if (!w.closed && w.document.hasFocus()) return w
    } catch {
      /* janela a fechar */
    }
  }
  return principal
}

/** `confirm` na janela onde a pessoa está (ver `janelaActiva`). */
export function confirmar(mensagem: string): boolean {
  return janelaActiva().confirm(mensagem)
}

export interface OpcoesDaJanela {
  /** Nome da janela do browser. Reabrir com o mesmo nome reaproveita a
   *  que existir; cada painel precisa do SEU (com nomes iguais, destacar
   *  um deitava o outro abaixo). */
  nome: string
  titulo: string
  features: string
  /** A utilizadora fechou a janela (X do browser, ou a janela foi
   *  recarregada). */
  aoFechar: () => void
  /** A janela de onde se abre. Por omissão, a global. */
  origem?: Window
}

export interface JanelaAberta {
  win: Window
  /** Onde o portal do React deve escrever. */
  container: HTMLDivElement
  /** Larga os listeners e fecha a janela (sem chamar `aoFechar`). */
  libertar: () => void
}

/**
 * Abre a janela e prepara-a. Devolve null quando o browser recusa
 * (bloqueador de janelas emergentes): quem chama mostra o painel no
 * sítio de sempre e avisa.
 *
 * Tem de ser chamada na mesma tarefa do gesto da utilizadora (um clique,
 * uma tecla), senão o browser trata-a como janela espontânea e bloqueia.
 */
export function abrirJanelaDestacada(o: OpcoesDaJanela): JanelaAberta | null {
  const origem = o.origem ?? (typeof window === 'undefined' ? null : window)
  if (!origem) return null
  let win: Window | null
  try {
    win = origem.open('', o.nome, o.features)
  } catch {
    win = null
  }
  if (!win) return null
  const w = win
  let container: HTMLDivElement
  let largarTema: () => void
  try {
    container = prepareDetachedDocument(w.document, o.titulo)
    copyStyleNodes(origem.document, w.document)
    largarTema = espelharTema(origem.document, w.document)
  } catch {
    // Uma janela que abriu mas cujo documento não se deixa preparar
    // (outra origem, por exemplo) é tão inútil como uma bloqueada.
    try {
      w.close()
    } catch {
      /* já fechada */
    }
    return null
  }

  abertas.add(w)
  const onClose = () => {
    abertas.delete(w)
    o.aoFechar()
  }
  w.addEventListener('beforeunload', onClose)
  // Recarregar ou fechar a página principal não pode deixar para trás
  // uma janela órfã com um painel morto lá dentro.
  const onHostUnload = () => {
    try {
      w.close()
    } catch {
      /* já fechada */
    }
  }
  origem.addEventListener('beforeunload', onHostUnload)

  let livre = false
  return {
    win: w,
    container,
    libertar: () => {
      if (livre) return
      livre = true
      abertas.delete(w)
      w.removeEventListener('beforeunload', onClose)
      origem.removeEventListener('beforeunload', onHostUnload)
      largarTema()
      try {
        w.close()
      } catch {
        /* já fechada */
      }
    },
  }
}
